import { useQuery } from '@tanstack/react-query';
import {
  type CalculatePolicyQuoteOutput,
  type PolicyControllerCalculatePolicyQuoteMutationRequest,
  policyControllerCalculatePolicyQuote,
  policyControllerCalculatePolicyQuoteQueryKey,
} from 'korint-sdk';

interface UseCalculatePolicyQuoteParams {
  policyId: string;
  branchId?: string;
  quoteOptions: PolicyControllerCalculatePolicyQuoteMutationRequest;
}

interface UseCalculatePolicyQuoteOptions<Return> {
  enabled?: boolean;
  silenceErrors?: boolean;
  select?: (data: CalculatePolicyQuoteOutput | undefined) => Return;
  onError?: (error: Error) => void | Promise<void>;
}

export function useCalculatePolicyQuote<Return = CalculatePolicyQuoteOutput>(
  { policyId, branchId, quoteOptions }: UseCalculatePolicyQuoteParams,
  { enabled, silenceErrors, select, onError }: UseCalculatePolicyQuoteOptions<Return> = {},
) {
  return useQuery<CalculatePolicyQuoteOutput | undefined, Error, Return>({
    queryKey: policyControllerCalculatePolicyQuoteQueryKey(policyId, quoteOptions, { branchId }),
    queryFn: async () => {
      try {
        const quote: CalculatePolicyQuoteOutput = await policyControllerCalculatePolicyQuote(
          policyId,
          quoteOptions,
          { branchId },
        );

        return quote;
      } catch (error) {
        await onError?.(error as Error);

        if (!silenceErrors) {
          throw error;
        }

        return;
      }
    },
    select,
    enabled,
    retry: false,
  });
}
